import React from "react";
import {
  AppBar,
  Toolbar,
  Typography,
  Box,
  Button,
  Select,
  MenuItem,
  useMediaQuery,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { format } from "date-fns";
import { useAtom } from "jotai";
import { themeAtom } from "../../-Atoms";
import { motion } from "framer-motion";

import getJobName from "../../GlobalFunctions/getJobName";

const PortalHeader = ({
  jobData,
  editMode,
  onBackToStoryboards,
  selectedCount = 0,
  selectsMade = false,
  onResetSelects,
  onConfirmSelects,
  gridColumns = 3,
  setGridColumns,
}) => {
  const [theme] = useAtom(themeAtom);
  const isSmallScreen = useMediaQuery("(max-width:1200px)");
  const isDarkMode = theme === "dark";
  const formatJobDate = (date) => {
    if (!date) return "";
    const jobDate = date.toDate ? date.toDate() : new Date(date);
    return format(jobDate, "MMMM do, yyyy");
  };

  const headerColors = isDarkMode
    ? {
        background: "rgba(17, 18, 22, 0.92)",
        border: "rgba(255, 255, 255, 0.06)",
        textPrimary: "#f1f2f6",
        textSecondary: "rgba(237, 239, 245, 0.7)",
        controlBg: "rgba(255, 255, 255, 0.04)",
      }
    : {
        background: "rgba(246, 247, 249, 0.95)",
        border: "rgba(15, 17, 23, 0.08)",
        textPrimary: "#0f1117",
        textSecondary: "rgba(15, 17, 23, 0.56)",
        controlBg: "rgba(15, 17, 23, 0.04)",
      };

  const buttonStyles = {
    textTransform: "none",
    borderRadius: 2,
    fontWeight: 600,
    px: 2.25,
    py: 0.65,
    fontSize: isSmallScreen ? "0.8rem" : "0.9rem",
    boxShadow: "none",
  };

  return (
    <AppBar
      component={motion.header}
      initial={{ opacity: 0, y: -12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.45, ease: "easeOut" }}
      position="static"
      elevation={1}
      sx={{
        background: headerColors.background,
        color: headerColors.textPrimary,
        borderBottom: `1px solid ${headerColors.border}`,
        boxShadow: "0 10px 28px rgba(0,0,0,0.08)",
        backdropFilter: "blur(12px)",
        zIndex: 8,
      }}
    >
      <Toolbar
        sx={{
          justifyContent: "space-between",
          minHeight: 84,
          py: 1.5,
          flexWrap: "wrap",
          gap: 2,
        }}
      >
        {/* Left section - Logo, job info, and controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 3,
            minWidth: editMode ? 460 : 280,
          }}
        >
          <img
            src="https://www.imagesthatsell.com.au/ITS_LOGO_SMALL.png"
            alt="ITS Logo"
            style={{
              height: 42,
              filter: theme === "dark" ? "brightness(0) invert(1)" : "none",
              opacity: 0.95,
            }}
          />
          <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
            <Typography
              variant="h6"
              component="h1"
              sx={{
                fontWeight: 600,
                color: headerColors.textPrimary,
                mb: 0.5,
                fontSize: isSmallScreen ? "1rem" : undefined,
              }}
            >
              {getJobName(jobData)}
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: headerColors.textSecondary,
                fontSize: isSmallScreen ? "0.75rem" : "0.875rem",
              }}
            >
              {formatJobDate(jobData.start)}
            </Typography>
          </Box>
        </Box>

        {/* Right section - Buttons */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            minWidth: editMode ? 480 : 180,
            justifyContent: "flex-end",
            flexWrap: "wrap",
          }}
        >
          {editMode && (
            <Select
              value={gridColumns}
              onChange={(e) => setGridColumns(e.target.value)}
              sx={{
                height: 36,
                width: 130,
                backgroundColor: headerColors.controlBg,
                color: headerColors.textPrimary,
                borderRadius: 2,
                "& .MuiSelect-icon": {
                  color: headerColors.textPrimary,
                },
              }}
              MenuProps={{
                disableScrollLock: true,
                PaperProps: {
                  sx: {
                    maxHeight: 300,
                  },
                },
              }}
            >
              <MenuItem value={1}>1 Across</MenuItem>
              <MenuItem value={2}>2 Across</MenuItem>
              <MenuItem value={3}>3 Across</MenuItem>
              <MenuItem value={4}>4 Across</MenuItem>
              <MenuItem value={5}>5 Across</MenuItem>
            </Select>
          )}
          {editMode ? (
            <>
              {!selectsMade && (
                <Button
                  variant="contained"
                  color="error"
                  sx={{
                    ...buttonStyles,
                    minWidth: 140,
                    backgroundColor: isDarkMode ? "#b3261e" : "#d32f2f",
                    color: "#fff",
                  }}
                  disabled={selectsMade}
                  onClick={onResetSelects}
                >
                  Reset Selects
                </Button>
              )}
              <Button
                variant="contained"
                color="primary"
                sx={{
                  ...buttonStyles,
                  minWidth: 200,
                  backgroundColor: isDarkMode ? "#1e88e5" : "#1976d2",
                  color: "#fff",
                }}
                disabled={selectsMade}
                onClick={onConfirmSelects}
              >
                {selectsMade
                  ? `Selects Confirmed (${selectedCount})`
                  : `Confirm Selects (${selectedCount})`}
              </Button>
              <Button
                variant="contained"
                startIcon={<ArrowBack />}
                onClick={onBackToStoryboards}
                sx={{
                  ...buttonStyles,
                  backgroundColor: isDarkMode ? "#2f3742" : "#e7e9ec",
                  color: isDarkMode ? "#f5f5f5" : "#1f2933",
                  px: 3,
                }}
              >
                {isSmallScreen ? "Back" : "Back to Files"}
              </Button>
            </>
          ) : (
            null
          )}
        </Box>
      </Toolbar>
    </AppBar>
  );
};

export default PortalHeader;
